import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import { useMemo, useRef } from "react";
import { Check } from "@mui/icons-material";

const SelectMultipleCheckboxInput = ({
  value = [],
  onChange,
  fullWidth = true,
  sx,
  options,
  size = "small",
  onBlur,
  label,
  placeholder,
  ChipProps,
  ...rest
}) => {
  const inputRef = useRef(null);

  const selectedOptions = useMemo(() => {
    return options.filter((option) =>
      isString(option) ? value.includes(option) : value.includes(option.value),
    );
  }, [value, options]);

  const styles = useMemo(() => {
    return {
      ".MuiInputBase-root": {
        gap: "5px",
      },
      ".MuiAutocomplete-tag": { margin: "0" },
      ".MuiAutocomplete-endAdornment": { marginRight: "5px" },
      ".MuiChip-root": {
        marginTop: 0,
        marginBottom: 0,
        ".MuiChip-label": {
          fontSize: "11.8px",
          fontWeight: 700,
          paddingLeft: "12px",
          paddingRight: "12px",
          color: "#33475b",
        },
      },
      ...sx,
    };
  }, [sx]);

  const emptyPlacholderValue = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }
    return "Start Typing...";
  }, [placeholder]);

  const isSelected = (option) => {
    const optionValue = isString(option) ? option : option.value;
    return value.includes(optionValue);
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      onBlur={onBlur}
      value={selectedOptions}
      fullWidth={fullWidth}
      size={size}
      options={options}
      sx={styles}
      onChange={(event, newValue) => {
        const selectedValues = newValue.map((option) =>
          isString(option) ? option : option.value,
        );
        onChange(selectedValues);

        if (inputRef.current) {
          inputRef.current.focus();
        }
      }}
      isOptionEqualToValue={(option, val) => {
        const optVal = isString(option) ? option : option.value;
        const valVal = isString(val) ? val : val.value;
        return optVal === valVal;
      }}
      getOptionLabel={(option) => (isString(option) ? option : option.label)}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const selected = isSelected(option);
        const label = isString(option) ? option : option.label;
        const keyValue = isString(option) ? option : option.value;

        return (
          <Box
            key={keyValue}
            {...optionProps}
            sx={{
              fontSize: ".7rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: selected ? 700 : 400,
              backgroundColor: selected
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
              "&:hover": {
                backgroundColor: selected
                  ? "rgba(25, 118, 210, 0.12) !important"
                  : undefined,
              },
            }}
          >
            {label}
            {selected && (
              <Check sx={{ fontSize: 16, color: "primary.main", ml: 1 }} />
            )}
          </Box>
        );
      }}
      ChipProps={ChipProps}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          placeholder={emptyPlacholderValue}
          inputRef={inputRef}
          endAdornment={params.InputProps.endAdornment}
          inputProps={{
            ...params.inputProps,
          }}
        />
      )}
    />
  );
};

export default SelectMultipleCheckboxInput;
